import type { FC, ReactElement } from "react";
import { useMemo, useState } from "react";

import { useSelector } from "react-redux";

import type { IconName } from "@similarweb/icons";
import { Dropdown, PlainTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { StaticListType } from "app/pages/sales-intelligence/domain/lists/types/static-list-model";
import { EnrichmentHistoryModal } from "app/pages/sales-intelligence/modules/enrichment/components/EnrichmentHistoryModal/EnrichmentHistoryModal";
import { DD_WIDTH } from "app/pages/sales-intelligence/modules/enrichment/constants/constants";
import useEnrichmentManagement from "app/pages/sales-intelligence/modules/enrichment/hooks/useEnrichmentManagement";
import useEnrichmentList from "app/pages/sales-intelligence/modules/enrichment/queries/useEnrichmentList";
import { EnrichmentStatus } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { selectExcelQuotaRemaining } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import { allTrackers } from "app/services/track/track";

import { IconWrapper, StyledEnrichmentDropdown, StyledEnrichmentOption } from "./style";

const OPTIONS = [
    {
        icon: "upload",
        key: "upload",
        label: "si.my-lists.section.enrichment.dropdown.option_1",
    },
    {
        icon: "",
        key: "results",
        label: "si.my-lists.section.enrichment.dropdown.option_2",
    },
];

type EnrichmentDropdownBaseProps = {
    trackingCategory: string;
    enrichmentButton: ReactElement;
    trackingResultList: readonly StaticListType[];
};

type EnrichmentDropdownProps = EnrichmentDropdownBaseProps & {
    onUploadCsv: () => void;
};

export const EnrichmentDropdown: FC<EnrichmentDropdownProps> = ({
    enrichmentButton,
    trackingCategory,
    trackingResultList,
    onUploadCsv,
}) => {
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const isOutOfQuota = useSelector(selectExcelQuotaRemaining) === 0;
    const { isOpen, onClickDisabledDD, setIsOpen } = useEnrichmentManagement();

    const { data } = useEnrichmentList();
    const validData = useMemo(() => data?.filter((item) => item.status === EnrichmentStatus.Succeeded), [data]);

    const isEnrichmentLocked = !salesSettingsHelper.isCsvEnrichmentEnabled();

    const onToggle = () => {
        if (!isOpen) {
            allTrackers.dispatchTrackEvent(
                trackingCategory,
                "open menu",
                `csv enrichment menu//${trackingResultList.length}`,
            );
        }

        setIsOpen(!isOpen);
    };

    const onClick = ({ id }: { id: string }) => {
        if (id === "upload") {
            allTrackers.dispatchTrackEvent(
                trackingCategory,
                "select menu option",
                `upload csv//${trackingResultList.length}`,
            );
            onUploadCsv();
        } else {
            allTrackers.dispatchTrackEvent(
                trackingCategory,
                "select menu option",
                `enrichment results//${trackingResultList.length}`,
            );
            setIsHistoryModalOpen(true);
        }
    };

    const options = [enrichmentButton].concat(
        OPTIONS.map(({ key, label, icon }) => {
            const isDisabled = key === "results" && !validData?.length;

            return (
                <StyledEnrichmentOption
                    id={key}
                    key={key}
                    disabled={isDisabled}
                >
                    {icon ? (
                        <IconWrapper
                            className="itemIcon"
                            iconName={icon as IconName}
                            size="sm"
                        />
                    ) : null}
                    <div>{translate(label)}</div>
                </StyledEnrichmentOption>
            );
        }),
    );

    const tooltipMessage = isEnrichmentLocked
        ? "si.my-lists.section.enrichment.dropdown.tooltip.no.access"
        : "si.my-lists.section.enrichment.dropdown.tooltip.not.enough.quota";

    const isDisabled = isEnrichmentLocked || isOutOfQuota;

    return (
        <PlainTooltip
            enabled={isDisabled}
            placement="top"
            tooltipContent={translate(tooltipMessage)}
        >
            <StyledEnrichmentDropdown
                className="enrichment-dd"
                data-automation="csv-enrichment-dropdown"
                onClick={onClickDisabledDD}
                disabled={isOutOfQuota}
            >
                <Dropdown
                    appendTo=".enrichment-dd"
                    width={DD_WIDTH}
                    onToggle={onToggle}
                    onClick={onClick}
                    disabled={isDisabled}
                >
                    {options}
                </Dropdown>
                <EnrichmentHistoryModal
                    isOpen={isHistoryModalOpen}
                    onOpenStateChange={setIsHistoryModalOpen}
                />
            </StyledEnrichmentDropdown>
        </PlainTooltip>
    );
};
